import { HttpResourceRef } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IApiErrorResponse } from '@core/models/api-response.model';
import {
  IApiTargetAppsProfiliApp,
  IApiTargetAppsProfiliGroup,
  IApiTargetAppsProfiliProfilo,
  ILandingAccessCard,
  ITargetApp,
  ITargetAppsData,
  ITargetAppWithProfiles
} from '@core/models/target-apps.model';
import { IUser } from '@core/models/user.model';
import { ApiService } from '@core/services/api/api.service';
import { environment } from '@environments/environment';
import { Console } from './logger.service';

/**
 * Servizio per la gestione delle target apps disponibili.
 *
 * Questo servizio si occupa di recuperare l'elenco delle applicazioni target
 * disponibili per il Single Sign-On (SSO).
 *
 * @example
 * constructor(private targetAppsService: TargetAppsService) {}
 *
 * // Recupera le target apps
 * this.targetAppsService.getTargetApps().subscribe({
 *   next: (apps) => console.log('Target apps:', apps),
 *   error: (error) => console.error('Errore:', error)
 * });
 */
@Injectable({
  providedIn: 'root'
})
export class TargetAppsService {
  private readonly log_label = 'TargetAppsService';
  private readonly apiService = inject(ApiService);
  private readonly GENERIC_ICON_PATH = '/images/app-icon-generic.svg';

  private readonly APP_LOGO_PATH = '/images/portale_app.svg';
  private readonly WEBMAIL_ICON_PATH = '/images/portale_webmail.svg';
  private readonly AMECONVIENE_ICON_PATH = '/images/portale_ameconviene.svg';
  private readonly PO_ICON_PATH = '/images/portale_po.svg';
  private readonly APP_PORTALE_KEY = 'app-portale';

  // Override temporaneo per test: ignora il campo "icona" ricevuto dal JSON.
  private readonly FORCE_EMPTY_JSON_ICON_FOR_TEST = false;
  /**
   * Lista delle chiavi delle app gestibili (deduplicata e ordinata),
   * ad esempio: "manifestazione", "convenzionato", "promotore", "app-portale", "po", "webmail".
   * Popolata ad ogni chiamata a getTargetApps() e utilizzabile
   * successivamente (es. nei modali) tramite getManagedAppNames().
   */
  private managedAppNames: string[] = [];
  
  /**
   * Recupera l'elenco delle target apps disponibili.
   * La lista è ordinata in senso crescente per chiave (`key`), locale `it`.
   *
   * @returns Observable contenente una lista di target apps
   * @throws {IApiErrorResponse} Se la richiesta fallisce
   */
  getTargetApps(): Observable<ITargetAppsData> {
    const log_pref = this.log_label + '.getTargetApps() ';
    
    // Chiamata API reale
    // NOTA: ApiService.get() già estrae i dati con extractData(), quindi response può essere:
    // - Array<Record<string, ITargetApp>> (se data è un array)
    // - Record<string, ITargetApp> (se data è un oggetto)
    return this.apiService.get<Array<Record<string, ITargetApp>> | Record<string, ITargetApp>>('sso/target-apps').pipe(
      map((response: Array<Record<string, ITargetApp>> | Record<string, ITargetApp>) => {
        // Trasforma la risposta in formato più semplice da usare.
        // La trasformazione popola anche la lista interna dei nomi gestibili.
        return this.transformResponse(response);
      }),
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nel recupero delle target apps.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Resource reattiva per le target apps con profili (Angular 22 `httpResource`).
   * La richiesta parte solo quando `shouldLoad()` è `true` (es. dopo verifica auth in landing).
   */
  createTargetAppsWithProfilesResource(
    shouldLoad: () => boolean
  ): HttpResourceRef<ITargetAppWithProfiles[]> {
    return this.apiService.getResource<ITargetAppWithProfiles[]>(
      () => (shouldLoad() ? 'sso/target-apps-profili' : undefined),
      undefined,
      {
        defaultValue: [],
        debugName: 'targetAppsWithProfiles',
        transform: (data) => this.transformTargetAppsProfiliResponse(
          data as IApiTargetAppsProfiliGroup[] | null | undefined
        )
      }
    );
  }

  /**
   * Recupera l'elenco delle target apps già raggruppate per nome con i profili annidati,
   * utilizzando la nuova API /sso/target-apps-profili.
   *
   * @returns Observable contenente una lista di ITargetAppWithProfiles
   * @throws {IApiErrorResponse} Se la richiesta fallisce
   */
  getTargetAppsWithProfiles(): Observable<ITargetAppWithProfiles[]> {
    const log_pref = this.log_label + '.getTargetAppsWithProfiles() ';

 
    // Chiamata API reale alla nuova endpoint
    return this.apiService.get<IApiTargetAppsProfiliGroup[]>('sso/target-apps-profili').pipe(
      map((response: IApiTargetAppsProfiliGroup[]) => {
        return this.transformTargetAppsProfiliResponse(response);
      }),
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || 'Errore nel recupero delle target apps con profili.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Restituisce la lista corrente dei nomi delle app gestibili.
   * La lista viene aggiornata ad ogni chiamata a getTargetApps().
   *
   * @returns Array di nomi app (senza duplicati)
   */
  getManagedAppNames(): string[] {
    return this.managedAppNames;
  }

  /**
   * Etichetta profilo per la landing (stessa regola del template raggruppato).
   */
  getProfileDisplayLabel(appNome: string, ruolo: string, username: string): string {
    if (ruolo.toLowerCase().trim() === appNome.toLowerCase().trim()) {
      return username;
    }
    return `${ruolo}\n${username}`;
  }

  /**
   * Esclude app-portale e i relativi profili dalla landing quando `NASCONDI_APP_PORTALE` è
   * attivo, salvo per gli utenti con ruolo admin.
   */
  applyLandingPortaleVisibility(
    groups: ITargetAppWithProfiles[],
    user: Pick<IUser, 'roles'> | null
  ): ITargetAppWithProfiles[] {
    if (!environment.NASCONDI_APP_PORTALE || user?.roles === 'admin') {
      return groups;
    }

    return groups
      .map((group) => {
        const profiles = group.profiles.filter((p) => p.key !== this.APP_PORTALE_KEY);
        const noProfileApp =
          group.noProfileApp?.key === this.APP_PORTALE_KEY ? null : group.noProfileApp;
        const keys = group.keys.filter((k) => k !== this.APP_PORTALE_KEY);

        return { ...group, profiles, noProfileApp, keys };
      })
      .filter((group) => group.profiles.length > 0 || group.noProfileApp);
  }

  /**
   * Trasforma i gruppi per app in card piatte (un profilo / una variante senza profilo per card).
   */
  toLandingAccessCards(groups: ITargetAppWithProfiles[]): ILandingAccessCard[] {
    const cards: ILandingAccessCard[] = [];

    groups.forEach((group) => {
      group.profiles.forEach((profile) => {
        cards.push({
          cardId: `profile:${profile.key}:${profile.id_profilo}`,
          kind: 'profile',
          key: profile.key,
          app: group.app,
          url: profile.url,
          id_profilo: profile.id_profilo,
          username: profile.username,
          ruolo: profile.ruolo
        });
      });

      if (group.noProfileApp) {
        const { key, app } = group.noProfileApp;
        cards.push({
          cardId: `no-profile:${key}`,
          kind: 'no-profile',
          key,
          app,
          url: app.url
        });
      }
    });

    return cards;
  }

  /**
   * Determina quale icona usare per una target app.
   * Se l'icona è vuota e il nome è "App", usa il logo principale.
   * Se l'icona è vuota e il nome è "Webmail", usa l'icona Webmail.
   * Se l'icona è vuota e il nome è "Portale Ameconviene.it", usa l'icona Ameconviene.
   * Altrimenti, se l'icona è vuota, usa l'icona generica.
   *
   * @param app La target app per cui determinare l'icona
   * @returns Il percorso dell'icona da utilizzare
   */
  private getIconPath(app: ITargetApp): string {
    const incomingIcon = this.FORCE_EMPTY_JSON_ICON_FOR_TEST ? '' : (app.icona ?? '');

    // Se l'icona è specificata e non vuota, usala
    if (incomingIcon.trim() !== '') {
      return incomingIcon;
    }
    
    // Se l'icona è vuota e il nome è "App", usa il logo principale
    if (app.nome === 'App') {
      return this.APP_LOGO_PATH;
    }
    
    // Se l'icona è vuota e il nome è "Webmail", usa l'icona Webmail
    if (app.nome === 'Webmail') {
      return this.WEBMAIL_ICON_PATH;
    }
    
    // Se l'icona è vuota e il nome è "Portale Ameconviene.it", usa l'icona Ameconviene
    if (app.nome === 'Portale Ameconviene.it Finance') {
      return this.AMECONVIENE_ICON_PATH;
    }
    
    // Se l'icona è vuota e il nome è "Portale Ameconviene.it", usa l'icona Ameconviene
    if (app.nome === 'Portale Agenzie') {
      return this.PO_ICON_PATH;
    }

    // Altrimenti usa l'icona generica
    return this.GENERIC_ICON_PATH;
  }

  /**
   * Trasforma il payload della nuova API /sso/target-apps-profili
   * in una lista di ITargetAppWithProfiles compatibile con la landing page.
   *
   * @param data Array di gruppi restituiti dall'API
   * @returns Array di ITargetAppWithProfiles
   */
  private transformTargetAppsProfiliResponse(
    data: IApiTargetAppsProfiliGroup[] | null | undefined
  ): ITargetAppWithProfiles[] {
    if (!data || !Array.isArray(data) || data.length === 0) {
      this.managedAppNames = [];
      return [];
    }

    const result: ITargetAppWithProfiles[] = [];
    const enabledKeysSet = new Set<string>();

    data.forEach((group: IApiTargetAppsProfiliGroup) => {
      if (!group || !Array.isArray(group.apps) || group.apps.length === 0) {
        return;
      }

      const apps = group.apps;

      // Usa la prima app come riferimento principale per il gruppo (nome, icona, abilitato)
      const mainApiApp: IApiTargetAppsProfiliApp = apps[0];
      const baseMainApp: ITargetApp = {
        url: mainApiApp.url,
        nome: mainApiApp.nome,
        descrizione: mainApiApp.descrizione,
        abilitato: mainApiApp.abilitato,
        senza_profilo: mainApiApp.senza_profilo,
        nuova_pagina: mainApiApp.nuova_pagina,
        icona: mainApiApp.icona ?? ''
      };

      const mainAppWithIcon: ITargetApp = {
        ...baseMainApp,
        icona: this.getIconPath(baseMainApp)
      };

      const groupKeys: string[] = [];
      let noProfileApp: { key: string; app: ITargetApp } | null = null;
      const profiles: ITargetAppWithProfiles['profiles'] = [];

      apps.forEach((apiApp: IApiTargetAppsProfiliApp) => {
        if (!apiApp) {
          return;
        }

        const key = apiApp.target_app_key;
        if (key && !groupKeys.includes(key)) {
          groupKeys.push(key);
        }

        // Aggiorna lista managedAppNames solo per app abilitate
        if (apiApp.abilitato === true && key && !enabledKeysSet.has(key)) {
          enabledKeysSet.add(key);
        }

        const baseApp: ITargetApp = {
          url: apiApp.url,
          nome: apiApp.nome,
          descrizione: apiApp.descrizione,
          abilitato: apiApp.abilitato,
          senza_profilo: apiApp.senza_profilo,
          nuova_pagina: apiApp.nuova_pagina,
          icona: apiApp.icona ?? ''
        };
        const appWithIcon: ITargetApp = {
          ...baseApp,
          icona: this.getIconPath(baseApp)
        };

        // Variante senza profilo (senza_profilo === true)
        if (apiApp.senza_profilo === true) {
          noProfileApp = {
            key,
            app: appWithIcon
          };
        }

        // Profili associati a questa app
        if (Array.isArray(apiApp.profili) && apiApp.profili.length > 0) {
          apiApp.profili.forEach((profilo: IApiTargetAppsProfiliProfilo) => {
            if (!profilo || profilo.bloccato === true) {
              return;
            }

            const exists = profiles.some(
              (p) => p.id_profilo === profilo.id_profilo && p.key === key
            );
            if (!exists) {
              profiles.push({
                id_profilo: profilo.id_profilo,
                id_segnalatore: profilo.id_segnalatore,
                username: profilo.username,
                ruolo: profilo.ruolo,
                key,
                url: appWithIcon.url,
                senza_profilo: false
              });
            }
          });
        }
      });

      // Includi il gruppo solo se ha almeno un profilo o una variante senza profilo
      if (profiles.length > 0 || noProfileApp) {
        result.push({
          nome: group.nome,
          keys: groupKeys,
          app: mainAppWithIcon,
          noProfileApp,
          profiles
        });
      }
    });

    // Aggiorna managedAppNames partendo dall'insieme delle chiavi abilitate
    this.managedAppNames = Array.from(enabledKeysSet);

    return result;
  }

  /**
   * Trasforma la risposta API in un formato più semplice da usare.
   * Gestisce sia array di oggetti che un singolo oggetto con chiavi dinamiche.
   * Converte in un array di ITargetAppItem.
   *
   * @param data Array di oggetti con chiavi dinamiche OPPURE un singolo oggetto con chiavi dinamiche che puntano a ITargetApp
   * @returns Array di ITargetAppItem con chiave e dati dell'app
   */
  private transformResponse(data: Array<Record<string, ITargetApp>> | Record<string, ITargetApp>): ITargetAppsData {
    if (!data) {
      return [];
    }

    const result: ITargetAppsData = [];

    // Se data è un array, itera su ogni elemento
    if (Array.isArray(data)) {
      data.forEach((item) => {
        // Itera su ogni chiave nell'oggetto
        Object.keys(item).forEach((key) => {
          const app = item[key];
          
          // Include tutte le app (non filtrare per abilitato qui)
          // Il controllo abilitato viene fatto solo quando necessario (es. nel click)
          if (app) {
            result.push({
              key: key,
              app: {
                ...app,
                // Determina l'icona da usare (logo principale se nome="App" e icona vuota, altrimenti generica)
                icona: this.getIconPath(app)
              }
            });
          }
        });
      });
    } else if (typeof data === 'object') {
      // Se data è un singolo oggetto, itera direttamente sulle sue chiavi
      Object.keys(data).forEach((key) => {
        const app = data[key];
        
        // Include tutte le app (non filtrare per abilitato qui)
        // Il controllo abilitato viene fatto solo quando necessario (es. nel click)
        if (app) {
          result.push({
            key: key,
            app: {
              ...app,
              // Determina l'icona da usare (logo principale se nome="App" e icona vuota, altrimenti generica)
              icona: this.getIconPath(app)
            }
          });
        }
      });
    }

    result.sort((a, b) => a.key.localeCompare(b.key, 'it', { sensitivity: 'base' }));

    // Crea/aggiorna la lista di chiavi delle app gestibili a partire dal risultato.
    // Logica:
    // - considera solo app abilitate (abilitato === true)
    // - usa la chiave dell'oggetto (es. "manifestazione", "convenzionato", "po", "webmail")
    // - rimuove i duplicati mantenendo l'ordine della lista già ordinata per chiave
    const seenKeys = new Set<string>();
    const enabledKeys: string[] = [];

    result.forEach(({ key, app }) => {
      if (!app) {
        return;
      }

      if (app.abilitato === true && !seenKeys.has(key)) {
        seenKeys.add(key);
        enabledKeys.push(key);
      }
    });

    this.managedAppNames = enabledKeys;

    return result;
  }
}
